import ReactMarkdown from 'react-markdown'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import 'katex/dist/katex.min.css'
import { parseLatexInText } from 'components/feature/Chat/SchoolGPT/SchoolGPTTexts'

const StyledTable = styled.table<{ isDarkTheme?: boolean }>`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin: 16px 0;
    border-radius: ${theme.borderRadius.normal};
    border: 1px solid ${({ isDarkTheme }) => (isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)')};
    overflow: hidden;
    color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.white : theme.colors.subtitleBlack)};
`

const StyledTh = styled.th<{ isDarkTheme?: boolean }>`
    background-color: ${theme.colors.backgroundTransparent};
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
`

const StyledTd = styled.td<{ isDarkTheme?: boolean }>`
    padding: 12px 16px;
    background-color: ${theme.colors.transparent};
    font-size: 14px;
    line-height: 1.5;
    border: 1px solid ${({ isDarkTheme }) => (isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)')};
`

const StyledTr = styled.tr<{ isDarkTheme?: boolean }>`
    background-color: transparent;
    border-radius: ${theme.borderRadius.normal};
`

const parseMarkdownWithTables = (text: string): Array<{ type: 'text' | 'table'; content: string }> => {
    const lines = text.split('\n')
    const segments: Array<{ type: 'text' | 'table'; content: string }> = []
    let currentSegment: string[] = []
    let inTable = false
    let tableLines: string[] = []

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const nextLine = lines[i + 1]

        if (line.includes('|') && nextLine && nextLine.includes('---')) {
            if (currentSegment.length > 0) {
                segments.push({ type: 'text', content: currentSegment.join('\n') })
                currentSegment = []
            }

            inTable = true
            tableLines = [line]
        } else if (inTable && line.includes('|')) {
            tableLines.push(line)
        } else if (inTable && line.includes('---')) {
            tableLines.push(line)
        } else if (inTable) {
            segments.push({ type: 'table', content: tableLines.join('\n') })
            tableLines = []
            inTable = false

            if (line.trim()) {
                currentSegment = [line]
            }
        } else {
            currentSegment.push(line)
        }
    }

    if (inTable && tableLines.length > 0) {
        segments.push({ type: 'table', content: tableLines.join('\n') })
    } else if (currentSegment.length > 0) {
        segments.push({ type: 'text', content: currentSegment.join('\n') })
    }

    return segments
}

const parseTableContent = (tableText: string) => {
    const lines = tableText.split('\n').filter((line) => line.trim().length > 0)

    if (lines.length < 2) return null

    const headerRow = lines[0]
        .split('|')
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0)

    const separatorRow = lines[1]
    if (!separatorRow.includes('---')) return null

    const bodyRows = lines.slice(2).map((row) =>
        row
            .split('|')
            .map((cell) => cell.trim())
            .filter((cell) => cell.length > 0)
    )

    return { headerRow, bodyRows }
}

const MarkdownWithTable = ({ children, isDarkTheme }: { children: string; isDarkTheme: boolean }) => {
    const segments = parseMarkdownWithTables(children)

    return (
        <>
            {segments.map((segment, index) => {
                if (segment.type === 'table') {
                    const tableData = parseTableContent(segment.content)

                    if (tableData) {
                        const { headerRow, bodyRows } = tableData

                        return (
                            <StyledTable key={index} isDarkTheme={isDarkTheme}>
                                <thead>
                                    <StyledTr isDarkTheme={isDarkTheme}>
                                        {headerRow.map((header, headerIndex) => (
                                            <StyledTh key={headerIndex} isDarkTheme={isDarkTheme}>
                                                {header}
                                            </StyledTh>
                                        ))}
                                    </StyledTr>
                                </thead>
                                <tbody>
                                    {bodyRows.map((row, rowIndex) => (
                                        <StyledTr key={rowIndex} isDarkTheme={isDarkTheme}>
                                            {row.map((cell, cellIndex) => (
                                                <StyledTd key={cellIndex} isDarkTheme={isDarkTheme}>
                                                    {cell}
                                                </StyledTd>
                                            ))}
                                        </StyledTr>
                                    ))}
                                </tbody>
                            </StyledTable>
                        )
                    }
                }

                return (
                    <ReactMarkdown
                        components={{
                            p: ({ children }) => <p>{parseLatexInText(children)}</p>,
                            li: ({ children }) => <li>{parseLatexInText(children)}</li>,
                        }}
                        key={index}
                    >
                        {segment.content}
                    </ReactMarkdown>
                )
            })}
        </>
    )
}

export default MarkdownWithTable
